import { safeFetchJSON } from "./http";

/* =============================================================
 * Base URL
 * ============================================================= */
const ENV_URL = String(
  process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_API ||
    "https://ia.fullmargin.net/"
)
  .trim()
  .replace(/\/+$/, "");

const HAS_API_SUFFIX = /\/api$/i.test(ENV_URL);

export const API_ORIGIN = HAS_API_SUFFIX ? ENV_URL.replace(/\/api$/i, "") : ENV_URL;
export const API_ROOT = `${API_ORIGIN}/api`;

function isAbsUrl(s = "") {
  return /^https?:\/\//i.test(String(s));
}

function buildPath(path) {
  if (!path) return `${API_ROOT}/`;
  if (isAbsUrl(path)) return path;

  const p = path.startsWith("/") ? path : `/${path}`;
  if (p.startsWith("/api/")) return `${API_ORIGIN}${p}`;
  return `${API_ROOT}${p}`;
}

/* =============================================================
 * Utils
 * ============================================================= */
export function getToken() {
  try {
    return localStorage.getItem("jwt_token");
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    if (!token) return;
    localStorage.setItem("jwt_token", String(token));
  } catch {}
}

export function clearToken() {
  try {
    localStorage.removeItem("jwt_token");
  } catch {}
}

function safeJsonStringify(obj) {
  try {
    return JSON.stringify(obj);
  } catch {
    return "{}";
  }
}

function pickErrorMessage(payload, status) {
  
  if (payload == null || payload === "") return `Server error: ${status}`;

  if (typeof payload === "string") return payload;

  const err = payload?.error;
  if (typeof err === "string") return err;
  if (err && typeof err === "object") return err.message || err.msg || safeJsonStringify(err);

  return payload?.message || payload?.msg || `Server error: ${status}`;
}

function normalizeProvider(marketOrProvider) {
  const v = String(marketOrProvider || "").trim().toLowerCase();
  if (!v) return "";
  if (v === "fx") return "forex";
  if (v === "finnhub") return "forex";
  return v;
}

/* =============================================================
 * Fetch 
 * ============================================================= */
async function request(
  path,
  {
    method = "GET",
    params = null,
    data = null,
    headers = {},
    token = null,              
    credentials = "include",
    signal,
  } = {}
) {
  const m = String(method || "GET").toUpperCase();
  const url = new URL(buildPath(path));

  if (params && typeof params === "object") {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    });
  }

  const h = { Accept: "application/json", ...headers };

  const jwt = token === false ? null : (String(token ?? getToken() ?? "").trim() || null);
  if (jwt) h.Authorization = `Bearer ${jwt}`;

  const hasBody = data != null && m !== "GET" && m !== "HEAD";
  let body = undefined;

  if (hasBody) {
    const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
    const isBlob = typeof Blob !== "undefined" && data instanceof Blob;

    if (isFormData || isBlob) {
      body = data;
    } else {
      h["Content-Type"] = h["Content-Type"] || "application/json";
      body = typeof data === "string" ? data : safeJsonStringify(data);
    }
  }

  let res;

const executeFetch = async (requestHeaders) => {
  return fetch(url.toString(), {
    method: m,
    headers: requestHeaders,
    credentials,
    body,
    signal,
  });
};

try {
  res = await executeFetch(h);

  /*
   * Le SSO utilise le cookie de session Flask.
   *
   * Si un ancien JWT enregistré dans localStorage provoque un 401,
   * on le supprime puis on retente une fois avec la session Flask.
   */
  if (res.status === 401 && jwt) {
    console.warn(
      "JWT local refusé : nouvelle tentative avec la session Flask.",
    );

    clearToken();

    const sessionHeaders = {
      ...h,
    };

    delete sessionHeaders.Authorization;

    res = await executeFetch(sessionHeaders);
  }
} catch (err) {
  if (err?.name === "AbortError") {
    return {
      error: "request_aborted",
      aborted: true,
    };
  }

  console.error("Network error:", err);

  return {
    error: "Network error: Unable to connect to the server.",
  };
}

  
  if (res.status === 204) return {};

  const ct = res.headers.get("content-type") || "";
  let payload = null;

  if (ct.includes("application/json")) {
    try {
      payload = await res.json();
    } catch {
      payload = null;
    }
  } else {
    const txt = await res.text().catch(() => "");
    payload = txt || null;
  }

  if (!res.ok) {
    const message = pickErrorMessage(payload, res.status);
    return { error: message, status: res.status, raw: payload };
  }

  
  if (typeof payload === "string") return { msg: payload };
  return payload ?? {};
}

/* =============================================================
 * SSE 
 * ============================================================= */
function buildSSEUrls(path, paramsObj) {
  const p = path.startsWith("/") ? path : `/${path}`;
  const q = new URLSearchParams(paramsObj || {}).toString();
  const withQ = (u) => (q ? `${u}?${q}` : u);

  
  const url1 = withQ(buildPath(p));
  
  const url2 = withQ(`${API_ORIGIN}${p}`);
  
  const url3 = withQ(`${API_ROOT}${p}`);

  const uniq = [];
  [url1, url2, url3].forEach((u) => {
    if (u && !uniq.includes(u)) uniq.push(u);
  });
  return uniq;
}

export function makeEventSource(path, paramsObj = {}, { withCredentials = true } = {}) {
  const urls = buildSSEUrls(path, paramsObj);
  let idx = 0;

  let es = new EventSource(urls[idx], { withCredentials });

  // listeners[type] = Map(fn -> opts)
  const listeners = new Map();

  const wrapper = {
    get url() {
      return es?.url;
    },
    get readyState() {
      return es?.readyState;
    },

    get onopen() {
      return wrapper._onopen;
    },
    set onopen(fn) {
      wrapper._onopen = fn;
      if (es) es.onopen = fn;
    },

    get onmessage() {
      return wrapper._onmessage;
    },
    set onmessage(fn) {
      wrapper._onmessage = fn;
      if (es) es.onmessage = fn;
    },

    get onerror() {
      return wrapper._onerror;
    },
    set onerror(fn) {
      wrapper._onerror = fn;
    
    },

    addEventListener(type, fn, opts) {
      if (!type || !fn) return;
      const map = listeners.get(type) || new Map();
      map.set(fn, opts);
      listeners.set(type, map);
      es?.addEventListener(type, fn, opts);
    },

    removeEventListener(type, fn, opts) {
      if (!type || !fn) return;
      const map = listeners.get(type);
      if (map) map.delete(fn);
      es?.removeEventListener(type, fn, opts);
    },

    close() {
      try {
        es?.close();
      } catch {}
    },

    _onopen: null,
    _onmessage: null,
    _onerror: null,
  };

  function reattachAll() {
    if (wrapper._onopen) es.onopen = wrapper._onopen;
    if (wrapper._onmessage) es.onmessage = wrapper._onmessage;

    for (const [type, map] of listeners.entries()) {
      for (const [fn, opts] of map.entries()) {
        try {
          es.addEventListener(type, fn, opts);
        } catch {}
      }
    }
  }

  function swapToNextUrl() {
    if (idx >= urls.length - 1) return false;
    idx += 1;

    try {
      es?.close();
    } catch {}

    es = new EventSource(urls[idx], { withCredentials });
    es.onerror = internalOnError;
    reattachAll();
    return true;
  }

  function internalOnError(ev) {
    const swapped = swapToNextUrl();

    try {
      if (typeof wrapper._onerror === "function") wrapper._onerror(ev);
    } catch {}

    if (!swapped) {
      // no-op
    }
  }

  es.onerror = internalOnError;
  return wrapper;
}



function unwrapAgentTokenResponse(response) {
  if (response?.error) {
    const error = new Error(
      response.error || "Erreur pendant la gestion du token Agent.",
    );

    error.status = response.status;
    error.payload = response.raw;

    throw error;
  }

  return response || {};
}

/**
 * Liste les tokens de l'utilisateur connecté.
 *
 * GET /api/agent/tokens
 */
export async function apiAgentListTokens() {
  const response = await request("/agent/tokens", {
    method: "GET",
  });

  return unwrapAgentTokenResponse(response);
}

/**
 * Crée un nouveau token.
 *
 * POST /api/agent/tokens
 */
export async function apiAgentCreateToken({
  label = "",
  active = true,
} = {}) {
  const response = await request("/agent/tokens", {
    method: "POST",
    data: {
      label: String(label || "").trim() || undefined,
      active: Boolean(active),
    },
  });

  return unwrapAgentTokenResponse(response);
}

/**
 * Modifie le nom ou l'état d'un token.
 *
 * PATCH /api/agent/tokens/:id
 */
export async function apiAgentUpdateToken(
  tokenId,
  payload = {},
) {
  if (tokenId === null || tokenId === undefined || tokenId === "") {
    throw new Error("L'identifiant du token est obligatoire.");
  }

  const data = {};

  if (Object.prototype.hasOwnProperty.call(payload, "label")) {
    data.label = String(payload.label || "").trim();
  }

  if (Object.prototype.hasOwnProperty.call(payload, "active")) {
    data.active = Boolean(payload.active);
  }

  const response = await request(
    `/agent/tokens/${encodeURIComponent(String(tokenId))}`,
    {
      method: "PATCH",
      data,
    },
  );

  return unwrapAgentTokenResponse(response);
}

/**
 * Modifie uniquement le nom du token.
 */
export async function apiAgentRenameToken(
  tokenId,
  label,
) {
  const cleanLabel = String(label || "").trim();

  if (!cleanLabel) {
    throw new Error("Le nom du token est obligatoire.");
  }

  return apiAgentUpdateToken(tokenId, {
    label: cleanLabel,
  });
}

/**
 * Active ou désactive un token.
 */
export async function apiAgentSetTokenActive(
  tokenId,
  active,
) {
  return apiAgentUpdateToken(tokenId, {
    active: Boolean(active),
  });
}

/**
 * Supprime définitivement un token.
 *
 * DELETE /api/agent/tokens/:id
 */
export async function apiAgentDeleteToken(tokenId) {
  if (tokenId === null || tokenId === undefined || tokenId === "") {
    throw new Error("L'identifiant du token est obligatoire.");
  }

  const response = await request(
    `/agent/tokens/${encodeURIComponent(String(tokenId))}`,
    {
      method: "DELETE",
    },
  );

  return unwrapAgentTokenResponse(response);
}



/* =============================================================
 * ✅ SSO (helpers)
 * ============================================================= */

function dedupe(arr) {
  const out = [];
  for (const x of arr) if (x && !out.includes(x)) out.push(x);
  return out;
}

function buildSSOStartCandidates(provider = "google", params = {}) {
  const p = String(provider || "google").trim();
  const q = new URLSearchParams(params || {}).toString();
  const withQ = (u) => (q ? `${u}?${q}` : u);

  // Chemins standards
  const a1 = withQ(`${API_ROOT}/auth/sso/${encodeURIComponent(p)}`);
  const a2 = withQ(`${API_ORIGIN}/auth/sso/${encodeURIComponent(p)}`);

  // Autre variante fréquente
  const b1 = withQ(`${API_ROOT}/sso/${encodeURIComponent(p)}`);
  const b2 = withQ(`${API_ORIGIN}/sso/${encodeURIComponent(p)}`);

  return dedupe([a1, a2, b1, b2]);
}

/**
 * @param {"api"|"origin"|"auto"} mode
 * 
 
 */
export async function apiSSOStartUrl(provider = "google", params = {}, { mode = "auto" } = {}) {
  const cands = buildSSOStartCandidates(provider, params);

  const preferApi = mode === "api" || (mode === "auto");
  const ordered =
    mode === "origin"
      ? cands.sort((u1, u2) => (u1.startsWith(API_ORIGIN) ? -1 : 1) - (u2.startsWith(API_ORIGIN) ? -1 : 1))
      : preferApi
      ? cands.sort((u1, u2) => (u1.startsWith(API_ROOT) ? -1 : 1) - (u2.startsWith(API_ROOT) ? -1 : 1))
      : cands;

  
  for (const url of ordered) {
    try {
      const r = await fetch(url, {
        method: "GET",
        credentials: "include",
        redirect: "manual",
      });
      
      if (r && (r.ok || (r.status >= 300 && r.status < 400))) return url;
      
      if (r && (r.status === 401 || r.status === 403)) return url;
      // 404 => on continue
    } catch {
      
      break;
    }
  }

  return ordered[0] || `${API_ROOT}/auth/sso/${encodeURIComponent(String(provider || "google"))}`;
}

export async function apiSSOStart(provider = "google", params = {}, opts = {}) {
  const url = await apiSSOStartUrl(provider, params, opts);
  window.location.assign(url);
}


export function consumeSSOTokenFromUrl(urlLike = window.location.href) {
  try {
    const url = new URL(String(urlLike));
    const qp = url.searchParams;

    const hp = new URLSearchParams(String(url.hash || "").replace(/^#/, ""));

    const token =
      qp.get("token") ||
      qp.get("jwt") ||
      qp.get("access_token") ||
      hp.get("token") ||
      hp.get("jwt") ||
      hp.get("access_token");

    if (token) {
      setToken(token);
      return { token };
    }
  } catch {}
  return null;
}

/* =============================================================
 * AUTH
 * ============================================================= */
export async function apiLogin(email, password) {
  const res = await request("/login", { method: "POST", data: { email, password } });
  if (!res?.error && res?.token) {
    setToken(res.token);
  }
  return res;
}

export async function apiRegister(username, email, password) {
  return request("/register", { method: "POST", data: { username, email, password } });
}

export function apiLogout() {
  clearToken();
}

export async function apiProfile() {
  return request("/me");
}

export async function apiHistory() {
  const r = await request("/history");
  if (r?.error && r?.status === 404) return [];
  return r;
}

/* =============================================================
 * CHAT / ANALYSE
 * ============================================================= */
export async function apiChat(message) {
  return request("/chat", { method: "POST", data: { message } });
}
export async function apiDeleteMessage(messageId) {
  return request(`/chat/${messageId}`, { method: "DELETE" });
}

export async function apiGetMarketData(symbol, outputSize = "full") {
  return request("/market_data", { params: { symbol, output_size: outputSize } });
}

export async function apiMarketAnalysisAI(prompt, symbol, analysisType) {
  return request("/market_analysis_ai", {
    method: "POST",
    data: { message: prompt, symbol, analysis_type: analysisType },
  });
}

/* =============================================================
 * MarketAnalyses
 * ============================================================= */
export async function apiCreateMarketAnalysis(payload) {
  return request("/market_analyses", { method: "POST", data: payload });
}

export async function apiGetMarketAnalyses(params = {}) {
  const q = {};
  if (params.analysis_type) q.analysis_type = params.analysis_type;
  if (params.symbol) q.symbol = params.symbol;
  if (typeof params.is_trading_order === "boolean") q.is_trading_order = String(params.is_trading_order);
  if (params.sort_by) q.sort_by = params.sort_by;
  if (params.sort_order) q.sort_order = params.sort_order;
  return request("/market_analyses", { params: q });
}

export async function apiGetSingleAnalysis(analysisId) {
  return request(`/market_analyses/${analysisId}`);
}

export async function apiDeleteMarketAnalysis(analysisId) {
  return request(`/market_analyses/${analysisId}`, { method: "DELETE" });
}

/* =============================================================
 * STRATEGIES + AI REVIEW
 * ============================================================= */
export async function apiGetStrategies() {
  const r = await request("/strategies");
  return r?.error ? { error: r.error } : r;
}

export async function apiSaveStrategy(strategy, token) {
  const isUpdate = !!strategy?.id;
  const endpoint = isUpdate ? `/strategies/${strategy.id}` : "/strategies";

  const payload = {
    ...strategy,
    markets: Array.isArray(strategy?.markets) ? strategy.markets : [],
    timeframes: Array.isArray(strategy?.timeframes) ? strategy.timeframes : [],
  };

  const res = await request(endpoint, { method: isUpdate ? "PUT" : "POST", data: payload, token });
  if (res?.error) throw new Error(res.error);
  return res?.strategy ?? res;
}

export async function apiDeleteStrategy(strategyId) {
  const r = await request(`/strategies/${strategyId}`, { method: "DELETE" });
  return r?.error ? { error: r.error } : r;
}

export async function apiToggleFavorite(strategyId) {
  const r = await request(`/strategies/${strategyId}/favorite`, { method: "POST" });
  if (r?.error) throw new Error(r.error);
  return r;
}

export async function apiGetStrategyReview(strategyId) {
  return request(`/strategies/${strategyId}/review`);
}

export async function apiStrategyAIReview(strategyOrId, { save = false } = {}) {
  const payload =
    typeof strategyOrId === "object"
      ? { strategy: strategyOrId, save }
      : { id: strategyOrId, save };
  return request("/strategies/ai_review", { method: "POST", data: payload });
}

export const apiAIReviewStrategy = apiStrategyAIReview;

/* =============================================================
 * AGENT (EA/MT5)
 * ============================================================= */
export function apiAgentRegister() {
  return request("/agent/register", { method: "POST" });
}
export function apiAgentRegisterExisting(agent_token) {
  return request("/agent/register", { params: { agent_token } });
}
export function apiAgentStatus(agent_token) {
  return request("/agent/status", { params: { agent_token } });
}
export function apiAgentOpenTrades(agent_token) {
  return request("/agent/open_trades", { params: { agent_token } });
}
export function apiAgentHistory(agent_token) {
  return request("/agent/history", { params: { agent_token } });
}
export function apiAgentPull(agent_token) {
  return request("/agent/pull", { params: { agent_token } });
}
export async function apiAgentCommand(agentToken, cmd) {
  return request("/agent/command", {
    method: "POST",
    params: { agent_token: agentToken },
    data: cmd,
  });
}
export function apiAgentReport(agent_token, payload) {
  return request("/agent/report", { method: "POST", params: { agent_token }, data: payload });
}
export async function apiAgentGetNotes(agent_token) {
  return request("/agent/notes", { params: { agent_token } });
}


export async function apiAgentUpsertNote(
  agent_token,
  {
    ticket,
    comment,
    tags = [],
    rating = 0,
    setup = "",
    attachments = [],
  }
) {
  return request("/agent/note", {
    method: "POST",
    params: { agent_token },
    data: {
      ticket,
      comment,
      tags,
      rating,
      setup,
      attachments,
    },
  });
}



/* =============================================================
 * CHALLENGE + SMART AGENT STATUS
 * ============================================================= */

export function getStoredAgentToken() {
  try {
    const keys = [
      "agent_token",
      "fullmetrix_agent_token",
      "fm_agent_token",
      "selected_agent_token",
      "last_agent_token",
      "agentToken",
    ];

    for (const key of keys) {
      const value = localStorage.getItem(key);
      if (value && String(value).trim()) return String(value).trim();
    }
  } catch {}

  return "";
}

export async function apiAgentTokens() {
  return request("/agent/tokens");
}

function normalizeAgentStatusPayload(payload = {}, fallbackToken = "") {
  const account = payload.account || payload.data?.account || {};

  const balance =
    payload.balance ??
    payload.account_balance ??
    account.balance ??
    account.account_balance ??
    0;

  const equity =
    payload.equity ??
    payload.account_equity ??
    account.equity ??
    account.account_equity ??
    balance;

  const broker =
    payload.broker ||
    payload.server ||
    account.broker ||
    account.server ||
    "";

  const login =
    payload.account_login ||
    payload.login ||
    account.account_login ||
    account.login ||
    "";

  return {
    ok: !payload.error,
    error: payload.error || null,
    token: payload.agent_token || payload.token || fallbackToken || "",
    connected: Boolean(payload.connected || payload.is_connected),
    queue_len: Number(payload.queue_len || 0),
    account,
    broker,
    server: broker,
    accountLogin: login,
    login,
    balance: Number(balance || 0),
    equity: Number(equity || 0),
    currency: payload.currency || account.currency || "USD",
    lastSync:
      payload.last_sync ||
      payload.last_heartbeat ||
      account.last_sync ||
      null,
    raw: payload,
  };
}

async function tryAgentStatusWithToken(agentToken) {
  const token = String(agentToken || "").trim();
  if (!token) return null;

  const res = await request("/agent/status", {
    params: { agent_token: token },
  });

  if (res?.error) return null;

  return normalizeAgentStatusPayload(res, token);
}

export async function apiAgentStatusForChallenge() {
  const localToken = getStoredAgentToken();

  if (localToken) {
    const status = await tryAgentStatusWithToken(localToken);
    if (status?.connected) return status;
  }

  const tokensRes = await apiAgentTokens();

  if (!tokensRes?.error) {
    const tokens = Array.isArray(tokensRes.tokens)
      ? tokensRes.tokens
      : Array.isArray(tokensRes.items)
        ? tokensRes.items
        : [];

    const activeTokens = tokens
      .filter((item) => item?.active !== false && item?.token)
      .sort((a, b) => {
        const ad = new Date(a.last_used_at || a.created_at || 0).getTime();
        const bd = new Date(b.last_used_at || b.created_at || 0).getTime();
        return bd - ad;
      });

    for (const item of activeTokens) {
      const status = await tryAgentStatusWithToken(item.token);

      if (status?.connected) {
        try {
          localStorage.setItem("agent_token", item.token);
          localStorage.setItem("fullmetrix_agent_token", item.token);
          localStorage.setItem("last_agent_token", item.token);
        } catch {}

        return status;
      }
    }

    if (activeTokens[0]?.token) {
      const status = await tryAgentStatusWithToken(activeTokens[0].token);

      if (status) {
        try {
          localStorage.setItem("agent_token", activeTokens[0].token);
          localStorage.setItem("fullmetrix_agent_token", activeTokens[0].token);
          localStorage.setItem("last_agent_token", activeTokens[0].token);
        } catch {}

        return status;
      }
    }
  }

  return {
    ok: false,
    connected: false,
    token: "",
    queue_len: 0,
    account: {},
    broker: "",
    server: "",
    accountLogin: "",
    login: "",
    balance: 0,
    equity: 0,
    currency: "USD",
    lastSync: null,
    error: "agent_not_connected",
  };
}

export async function apiChallengeList() {
  return request("/challenges");
}

export async function apiChallengeCreate(payload) {
  return request("/challenges", {
    method: "POST",
    data: payload || {},
  });
}

export async function apiChallengeSync(challengeId) {
  return request(`/challenges/${encodeURIComponent(String(challengeId))}/sync`, {
    method: "POST",
    data: {},
  });
}

export async function apiChallengeDelete(challengeId) {
  return request(`/challenges/${encodeURIComponent(String(challengeId))}`, {
    method: "DELETE",
  });
}



export async function apiForexCandles({ symbol, granularity, count = 1000, end, start } = {}) {
  return apiBacktestCandles({
    market: "forex",
    symbol,
    granularity,
    count,
    start,
    end,
  });
}


/* =============================================================
 * JOURNAL
 * ============================================================= */
export async function apiJournalList() {
  return request("/agent/journal");
}
export async function apiJournalGet(id) {
  return request(`/agent/journal/${id}`);
}
export async function apiJournalSave({ name, history, notes }) {
  return request("/agent/journal", { method: "POST", data: { name, history, notes } });
}
export async function apiJournalUpdate(id, payload) {
  return request(`/agent/journal/${id}`, { method: "PUT", data: payload });
}
export async function apiJournalDelete(id) {
  return request(`/agent/journal/${id}`, { method: "DELETE" });
}

/* =============================================================
 * BACKTEST (Deriv + Forex Finnhub) ✅
 * ============================================================= */
export async function apiBacktestPing() {
  return request("/backtest/ping");
}

export async function apiBacktestCandles({
  market, // "deriv" | "forex"
  provider, // alias
  symbol,
  granularity,
  start,
  end,
  count,
  all,
  max,
  batch,
} = {}) {
  const params = {};

  if (symbol) params.symbol = symbol;
  if (granularity !== undefined && granularity !== null) params.granularity = String(granularity);

  if (start != null && start !== "") params.start = String(start);
  if (end != null && end !== "") params.end = String(end);

  if (count != null && count !== "") params.count = String(count);

  const prov = normalizeProvider(market || provider);
  if (prov) {
    
    params.provider = prov;
    params.market = prov;
  }

  if (all) params.all = "1";
  if (max) params.max = String(max);
  if (batch) params.batch = String(batch);

  return request("/backtest/candles", { params });
}

export async function apiBacktestTicks({ market, provider, symbol, start, end, count } = {}) {
  const params = {};
  if (symbol) params.symbol = symbol;
  if (start != null && start !== "") params.start = String(start);
  if (end != null && end !== "") params.end = String(end);
  if (count != null && count !== "") params.count = String(count);

  const prov = normalizeProvider(market || provider);
  if (prov) {
    params.provider = prov;
    params.market = prov;
  }
  return request("/backtest/ticks", { params });
}

export function apiBacktestStream({ market, provider, symbol, granularity } = {}) {
  const prov = normalizeProvider(market || provider);
  const params = {
    symbol,
    granularity: String(granularity),
    ...(prov ? { provider: prov, market: prov } : {}),
  };
  return makeEventSource("/backtest/stream", params);
}

/* Helpers compat (si ton UI les appelle encore) */
export async function apiBacktestRange({ symbol, granularity, max = 150000, batch = 5000 } = {}) {
  return request("/backtest/range", { params: { symbol, granularity, max, batch } });
}
export async function apiBacktestLatest({ symbol, granularity } = {}) {
  const params = {};
  if (symbol) params.symbol = symbol;
  if (granularity !== undefined && granularity !== null) params.granularity = String(granularity);
  return request("/backtest/latest", { params });
}
export async function apiBacktestChunk({ symbol, granularity, size, end } = {}) {
  const params = {};
  if (symbol) params.symbol = symbol;
  if (granularity !== undefined && granularity !== null) params.granularity = String(granularity);
  if (size !== undefined && size !== null) params.size = String(size);
  if (end !== undefined && end !== null) params.end = String(end);
  return request("/backtest/chunk", { params });
}



/* =============================================================
 * LICENCE FULLMETRIX EA
 * ============================================================= */

/**
 * Récupère l'abonnement et la licence de l'utilisateur connecté.
 *
 * Backend :
 * GET /api/fmmetrix/license
 */
export async function apiGetFmmetrixLicense({ signal } = {}) {
  return request("/fmmetrix/license", {
    method: "GET",
    signal,
  });
}

/**
 * Demande au backend de relancer la synchronisation :
 * - génération de la licence si elle n'existe pas ;
 * - renouvellement si une nouvelle période a été payée ;
 * - aucune action si la période est déjà synchronisée.
 *
 * Backend :
 * POST /api/fmmetrix/license/sync
 */
export async function apiSyncFmmetrixLicense({ signal } = {}) {
  return request("/fmmetrix/license/sync", {
    method: "POST",
    data: {},
    signal,
  });
}



/* =============================================================
 * 
 * ============================================================= */
export async function apiTvChunk(params) {
  const q = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v != null) q[k] = String(v);
  });
  return request("/tv/chunk", { params: q });
}

/* =============================================================
 * 
 * ============================================================= */
export async function apiParseMessage(text, opts = {}) {
  const keepSymbol = (s = "") => s.toString().trim().replace(/\s+/g, " ").toUpperCase();
  const fnum = (v) => {
    if (v == null || v === "") return undefined;
    const n = parseFloat(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
  };
  const fint = (v) => {
    if (v == null || v === "") return undefined;
    const n = parseInt(String(v), 10);
    return Number.isFinite(n) ? n : undefined;
  };
  const clean = (o) =>
    Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== ""));

  function guessSymbol(s = "") {
    const deriv = s.match(/\b(FX\s+VOL\s+\d+|VOL\s+\d+|BOOM\s+\d+|CRASH\s+\d+|JUMP\s+\d+)\b/ig);
    if (deriv?.length) return keepSymbol(deriv[0]);
    const label = s.match(/(?:symbol|instrument)\s*[:=]\s*([A-Z0-9 _-]{3,})/i)?.[1];
    if (label) return keepSymbol(label);
    const sur = s.match(/\bsur\s+([A-Z0-9 _-]{3,})/i)?.[1];
    if (sur) return keepSymbol(sur);
    const compact = s.match(/\b([A-Z]{3,6}\d{0,3})\b/g);
    if (compact?.length) return keepSymbol(compact[0]);
    return "";
  }

  function toOrderShape(input, originalText) {
    const i = input?.order ?? input?.data ?? input ?? {};
    const instrument =
      keepSymbol(i.instrument || i.symbol || i.market || opts.defaultInstrument || "") ||
      keepSymbol(guessSymbol(originalText));

    const actionTxt = String(i.action || i.side || i.cmd || "").toLowerCase();
    return {
      order: clean({
        action: actionTxt,
        instrument,
        side: i.side?.toLowerCase(),
        lot_size: fnum(i.lot_size ?? i.lots ?? i.volume ?? i.size),
        sl_points: fint(i.sl_points ?? i.stop_loss_points ?? i.sl ?? i.stop_loss),
        tp_points: fint(i.tp_points ?? i.take_profit_points ?? i.tp ?? i.take_profit),
        sl_price: fnum(i.sl_price ?? i.stop_loss_price),
        tp_price: fnum(i.tp_price ?? i.take_profit_price),
        order_type: String(i.order_type ?? i.pending_type ?? "").toLowerCase(),
        price: fnum(i.price ?? i.entry_price),
        offset_points: fint(i.offset_points ?? i.be_plus ?? i.breakeven_plus),
        points: fint(i.points ?? i.trail_points),
        ticket: fint(i.ticket ?? i.order_ticket),
        only_in_profit: typeof i.only_in_profit === "boolean" ? i.only_in_profit : undefined,
      }),
    };
  }

  async function hit(path, { method = "POST", params, body } = {}) {
    const url = new URL(buildPath(path));
    if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    const headers = { "Content-Type": "application/json", Accept: "application/json" };

    try {
      const t = getToken();
      if (t) headers.Authorization = `Bearer ${t}`;
    } catch {}

    let res;
    try {
      res = await fetch(url.toString(), {
        method,
        headers,
        credentials: "include",
        body: body ? safeJsonStringify(body) : undefined,
      });
    } catch {
      return { error: "network" };
    }

    const ct = res.headers.get("content-type") || "";
    const isJson = ct.includes("application/json");
    let data = null;

    try {
      data = isJson ? await res.json() : await res.text();
    } catch {}

    if (!res.ok) {
      const msg = isJson ? (data?.error?.message || data?.error || data?.message) : data;
      return { error: msg || `HTTP ${res.status}` };
    }
    return data ?? {};
  }

  const normalizeAction = (x) => {
    const act = String(x?.action || x?.order?.action || "").toLowerCase().trim();
    if (!act) return x;
    const map = {
      close_all_positions: "close_all",
      close_all_orders: "close_all",
      close_positions: "close_all",
      close_everything: "close_all",
      closeall: "close_all",
      close_all: "close_all",
    };
    const na = map[act] || act;
    if (x.order) return { ...x, order: { ...x.order, action: na } };
    return { ...x, action: na };
  };

  const attempts = [
    { m: "POST", p: "/parse", body: { text } },
    { m: "POST", p: "/parse-message", body: { text } },
    { m: "POST", p: "/chat/parse", body: { text } },
    { m: "GET", p: "/parse", params: { text } },
    { m: "GET", p: "/parse-message", params: { text } },
  ];

  for (const t of attempts) {
    const resp = await hit(t.p, { method: t.m, params: t.params, body: t.body });
    if (resp && !resp.error) {
      const fixed = normalizeAction(resp);
      return toOrderShape(fixed, text);
    }
  }

  
  const lower = String(text || "").toLowerCase();
  const instrument = guessSymbol(text) || keepSymbol(opts.defaultInstrument || "");

  if (/\b(close|ferme(r)?)\b.*\b(all|tout(e|es)?s?)\b/.test(lower)) return { order: { action: "close_all" } };
  if (/\b(close|ferme(r)?)\b/.test(lower)) return { order: { action: "close", instrument } };

  if (/\b(breakeven|be)\b/.test(lower)) {
    const plus = text.match(/(?:\+|plus\s*)?(-?\d+)\s*(?:pts?|points?|pips?)\b/i)?.[1];
    const onlyProf =
      /\bonly[_\s-]?in[_\s-]?profit\b|seulement\s+si\s+profit|que\s+si\s+profit/i.test(lower);
    return { order: clean({ action: "breakeven", instrument, offset_points: fint(plus), only_in_profit: onlyProf }) };
  }

  if (/\b(trailing|trail)\b/.test(lower)) {
    const pts = text.match(/(\d+)\s*(?:pts?|points?|pips?)\b/i)?.[1];
    return { order: clean({ action: "trailing", instrument, points: fint(pts) }) };
  }

  const modifyVerb = /(modif|change|set|mets?|place|fixe|ajust)/i.test(lower);
  if (modifyVerb || /\b(tp|take\s*profit|sl|stop\s*loss)\b/i.test(lower)) {
    const tpPts = text.match(/\btp\b\s*(?:sur|a|à|=|:)?\s*([0-9]+)\s*(?:pts?|points?|pips?)\b/i)?.[1];
    const tpPrc =
      text.match(/\btp\b\s*(?:@|at|sur|a|à|=|:)?\s*([0-9]+(?:[.,][0-9]+)?)\b(?!\s*(?:pts?|points?|pips?))/i)?.[1];
    const slPts = text.match(/\bsl\b\s*(?:sur|a|à|=|:)?\s*([0-9]+)\s*(?:pts?|points?|pips?)\b/i)?.[1];
    const slPrc =
      text.match(/\bsl\b\s*(?:@|at|sur|a|à|=|:)?\s*([0-9]+(?:[.,][0-9]+)?)\b(?!\s*(?:pts?|points?|pips?))/i)?.[1];

    const hasSymbol = !!instrument;
    return {
      order: clean({
        action: hasSymbol ? "modify" : "modify_all",
        instrument: hasSymbol ? instrument : undefined,
        tp_points: fint(tpPts),
        sl_points: fint(slPts),
        tp_price: fnum(tpPrc),
        sl_price: fnum(slPrc),
      }),
    };
  }

  if (/\b(buy|sell)\s+(limit|stop)\b/i.test(text)) {
    const side = text.match(/\b(buy|sell)\b/i)?.[1]?.toLowerCase();
    const typ = text.match(/\b(limit|stop)\b/i)?.[1]?.toLowerCase();
    const lots = fnum(
      text.match(/\b(?:lot|lots|volume|size)\s*([0-9]+(?:[.,][0-9]+)?)\b/i)?.[1] ||
        text.match(/([0-9]+(?:[.,][0-9]+)?)\s*(?:lot|lots)\b/i)?.[1]
    );
    const price = fnum(text.match(/\b(?:@|at|sur|a|à|=|:)\s*([0-9]+(?:[.,][0-9]+)?)\b/i)?.[1]);
    const slPts = fint(text.match(/\bsl\s+(\d+)\s*(?:pts?|points?|pips?)\b/i)?.[1]);
    const tpPts = fint(text.match(/\btp\s+(\d+)\s*(?:pts?|points?|pips?)\b/i)?.[1]);
    const slPrc = fnum(text.match(/\bsl\s*@\s*([0-9]+(?:[.,][0-9]+)?)\b/i)?.[1]);
    const tpPrc = fnum(text.match(/\btp\s*@\s*([0-9]+(?:[.,][0-9]+)?)\b/i)?.[1]);

    return {
      order: clean({
        action: "pending",
        side,
        instrument,
        lot_size: lots,
        order_type: `${side}_${typ}`,
        price,
        sl_points: slPts,
        tp_points: tpPts,
        sl_price: slPrc,
        tp_price: tpPrc,
      }),
    };
  }

  if (/\b(buy|sell|acheter|vendre|ach(?:e|è)te|vend)\b/i.test(text)) {
    const side = /sell|vendre|vend/i.test(text) ? "sell" : "buy";
    const lots = fnum(
      text.match(/\b(?:lot|lots|volume|size)\s*([0-9]+(?:[.,][0-9]+)?)\b/i)?.[1] ||
        text.match(/([0-9]+(?:[.,][0-9]+)?)\s*(?:lot|lots)\b/i)?.[1]
    );
    const slPts = fint(text.match(/\bsl\s+(\d+)\s*(?:pts?|points?|pips?)\b/i)?.[1]);
    const tpPts = fint(text.match(/\btp\s+(\d+)\s*(?:pts?|points?|pips?)\b/i)?.[1]);
    const slPrc = fnum(text.match(/\bsl\s*@\s*([0-9]+(?:[.,][0-9]+)?)\b/i)?.[1]);
    const tpPrc = fnum(text.match(/\btp\s*@\s*([0-9]+(?:[.,][0-9]+)?)\b/i)?.[1]);

    return {
      order: clean({
        action: side,
        instrument,
        lot_size: lots ?? 0.1,
        sl_points: slPts,
        tp_points: tpPts,
        sl_price: slPrc,
        tp_price: tpPrc,
      }),
    };
  }

  return { order: {} };
}

/* =============================================================
 * MAPPING 
 * ============================================================= */
export function buildEACommand(parsed) {
  const toNum = (v) => {
    if (v == null || v === "") return undefined;
    const n = parseFloat(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
  };
  const keep = (s = "") => s.toString().trim().replace(/\s+/g, " ").toUpperCase();

  const o = parsed?.order || parsed?.data || parsed || {};
  const action = String(o.action || o.side || o.cmd || "").toLowerCase();
  const symbol = keep(o.instrument || o.symbol || "");
  const volume = toNum(o.lot_size ?? o.volume ?? o.size);
  const sl_points = toNum(o.sl_points ?? o.stop_loss_points ?? o.stop_loss);
  const tp_points = toNum(o.tp_points ?? o.take_profit_points ?? o.tp ?? o.take_profit);
  const sl_price = toNum(o.sl_price ?? o.stop_loss_price);
  const tp_price = toNum(o.tp_price ?? o.take_profit_price);
  const price = toNum(o.price ?? o.entry_price);
  const order_type = String(o.order_type || o.pending_type || "").toLowerCase();
  const side = String(o.side || (action === "buy" || action === "sell" ? action : undefined) || "").toLowerCase();
  const ticket = toNum(o.ticket);
  const offset_points = toNum(o.offset_points ?? o.be_plus ?? o.breakeven_plus);
  const points = toNum(o.points ?? o.trail_points);
  const only_in_profit = !!o.only_in_profit;

  const clean = (obj) => {
    Object.keys(obj).forEach((k) => (obj[k] === undefined || obj[k] === "") && delete obj[k]);
    return obj;
  };

  switch (action) {
    case "buy":
    case "sell":
      return clean({
        cmd: "market",
        side,
        symbol: symbol || "EURUSD",
        volume: volume ?? 0.1,
        sl_points,
        tp_points,
        sl_price,
        tp_price,
      });
    case "pending":
      return clean({
        cmd: "pending",
        side,
        symbol: symbol || "EURUSD",
        volume: volume ?? 0.1,
        order_type,
        price,
        sl_points,
        tp_points,
        sl_price,
        tp_price,
      });
    case "modify":
      return clean({ cmd: "modify", symbol, sl_points, tp_points, sl_price, tp_price });
    case "modify_all":
      return clean({ cmd: "modify_all", sl_points, tp_points, sl_price, tp_price });
    case "close_all":
      return { cmd: "close_all" };
    case "close":
      return clean({ cmd: "close", symbol, ticket });
    case "breakeven":
      return clean({ cmd: "breakeven", symbol, offset_points, only_in_profit });
    case "trailing":
      return clean({ cmd: "trailing", symbol, points });
    default:
      break;
  }

  if (tp_points != null || sl_points != null || tp_price != null || sl_price != null) {
    if (symbol) return clean({ cmd: "modify", symbol, sl_points, tp_points, sl_price, tp_price });
    return clean({ cmd: "modify_all", sl_points, tp_points, sl_price, tp_price });
  }
  return null;
}

/* =============================================================
 
 * ============================================================= */
export async function fetchAPI(endpoint, method = "GET", data = null, token = null) {
  return request(endpoint, { method, data, token });
}

/* =============================================================
 * 
 * ============================================================= */
export async function apiAgentWizardExecute(agentToken, payload) {
  const r = await request("/agent/wizard/execute", {
    method: "POST",
    data: payload,
    headers: {
      ...(agentToken ? { "X-Agent-Token": agentToken } : {}),
    },
  });

  if (r?.error && r?.status === 404) {
    const url = buildPath("/api/agent/wizard/execute");
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(agentToken ? { "X-Agent-Token": agentToken } : {}),
        },
        body: safeJsonStringify(payload),
        credentials: "include",
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { error: pickErrorMessage(data, res.status), status: res.status, raw: data };
      return data ?? {};
    } catch (e) {
      return { error: e?.message || "Network error" };
    }
  }

  return r;
}



function normalizeDownloadUrl(u) {
  if (!u) return "";
  const s = String(u).trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return s.startsWith("/") ? s : `/${s}`;
}

/* ----------------------------- COPIER ----------------------------- */
export async function apiCopyMe() {
  return request("/copy/me");
}

export async function apiCopyMasters() {
  return request("/copy/masters");
}

export async function apiCopySubscribe(masterId) {
  return request("/copy/subscribe", {
    method: "POST",
    data: { master_id: masterId },
  });
}


export async function apiCopyUnsubscribe() {
  return request("/copy/unsubscribe", { method: "POST", data: {} });
}

export async function apiCopySaveConfig(config) {
  return request("/copy/config", {
    method: "POST",
    data: { config: config || {} },
  });
}


export async function apiCopyGetAgentDownloadUrl({
  preferBackend = true,
  publicPath = "/agents/CopyFollower.ex5",
} = {}) {
  return normalizeDownloadUrl(preferBackend ? "/api/copy/agent/download" : publicPath);
}

export async function apiCopyOpenAgentDownload(opts = {}) {
  const url = await apiCopyGetAgentDownloadUrl(opts);
  window.open(url, "_blank", "noopener,noreferrer");
  return { url };
}

/* ----------------------------- MASTER ----------------------------- */
export async function apiCopyMasterEnable(payload = {}) {
  return request("/copy/master/enable", { method: "POST", data: payload || {} });
}

export async function apiCopyMasterMe() {
  return request("/copy/master/me");
}

export async function apiCopyMasterRotateToken() {
  return request("/copy/master/rotate_token", { method: "POST", data: {} });
}

export async function apiCopyMasterFollowers() {
  return request("/copy/master/followers");
}

/** NEW: update master profile (backend: PATCH /api/copy/master/profile) */
export async function apiCopyMasterUpdateProfile(payload) {
  return request("/copy/master/profile", {
    method: "PATCH",
    data: payload || {},
  });
}

/* Download master agent */
export async function apiCopyGetMasterAgentDownloadUrl({
  preferBackend = true,
  publicPath = "/agents/CopyMaster.ex5",
} = {}) {
  return normalizeDownloadUrl(preferBackend ? "/api/copy/master/agent/download" : publicPath);
}

export async function apiCopyOpenMasterAgentDownload(opts = {}) {
  const url = await apiCopyGetMasterAgentDownloadUrl(opts);
  window.open(url, "_blank", "noopener,noreferrer");
  return { url };
}


export function apiCopyMasterPublicTokens() {
  return request("/copy/master/public_tokens");
}

export function apiCopyMasterCreatePublicToken(label = "") {
  return request("/copy/master/public_tokens", {
    method: "POST",
    data: { label: label || "" },
  });
}

export function apiCopyMasterDeletePublicToken(id) {
  return request(`/copy/master/public_tokens/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    data: {},
  });
}


/* ----------------------------- HEALTH / DEBUG ----------------------------- */
export async function apiCopyHealth() {
  return request("/copy/health");
}

export async function apiCopyPing() {
  const r = await apiCopyMasters();
  if (r?.error) return { ok: false, error: r.error, status: r.status };
  return { ok: true };
}





/* =============================================================
 * ANALYSE FONDAMENTALE
 * ============================================================= */

/**
 * Vérifie que le service fondamental fonctionne.
 *
 * Backend :
 * GET /api/fundamental/health
 */
export async function apiFundamentalHealth({
  signal,
} = {}) {
  return request("/fundamental/health", {
    method: "GET",
    signal,
  });
}


/**
 * Charge toutes les données du dashboard fondamental.
 *
 * Backend :
 * GET /api/fundamental/dashboard
 */
export async function apiGetFundamentalDashboard({
  start,
  end,
  importance = 1,
  refresh = false,
  signal,
} = {}) {
  return request("/fundamental/dashboard", {
    method: "GET",

    params: {
      start,
      end,
      importance,
      refresh: refresh ? "1" : undefined,
    },

    signal,
  });
}


/**
 * Charge le calendrier économique.
 *
 * Backend :
 * GET /api/fundamental/calendar
 */
export async function apiGetFundamentalCalendar({
  start,
  end,
  importance = 1,
  currency,
  status,
  signal,
} = {}) {
  return request("/fundamental/calendar", {
    method: "GET",

    params: {
      start,
      end,
      importance,
      currency,
      status,
    },

    signal,
  });
}


/**
 * Charge le classement de toutes les devises.
 *
 * Backend :
 * GET /api/fundamental/currencies
 */
export async function apiGetFundamentalCurrencies({
  signal,
} = {}) {
  return request("/fundamental/currencies", {
    method: "GET",
    signal,
  });
}


/**
 * Charge l'analyse détaillée d'une devise.
 *
 * Exemple :
 * apiGetFundamentalCurrency("USD")
 *
 * Backend :
 * GET /api/fundamental/currencies/USD
 */
export async function apiGetFundamentalCurrency(
  currencyCode,
  {
    signal,
  } = {},
) {
  const code = String(
    currencyCode || "",
  )
    .trim()
    .toUpperCase();

  if (!code) {
    return {
      error: "Le code de la devise est obligatoire.",
      status: 400,
    };
  }

  return request(
    `/fundamental/currencies/${encodeURIComponent(code)}`,
    {
      method: "GET",
      signal,
    },
  );
}


/**
 * Charge l'analyse détaillée d'un marché.
 *
 * Accepte :
 * EUR/USD
 * EURUSD
 * EUR-USD
 *
 * Backend :
 * GET /api/fundamental/markets/EURUSD
 */
export async function apiGetFundamentalMarket(
  marketSymbol,
  {
    signal,
  } = {},
) {
  const symbol = String(
    marketSymbol || "",
  )
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

  if (!symbol) {
    return {
      error: "Le symbole du marché est obligatoire.",
      status: 400,
    };
  }

  return request(
    `/fundamental/markets/${encodeURIComponent(symbol)}`,
    {
      method: "GET",
      signal,
    },
  );
}
